/**
 * Calendar Event List Page
 * Public discovery page for browsing events with Outlook-style calendar views
 */

import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Pagination,
  IconButton,
  TextField,
  InputAdornment,
  Stack,
  Chip,
  CircularProgress,
} from '@mui/material'
import { Add, FilterList, Search, Event } from '@mui/icons-material'
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  startOfDay,
  endOfDay,
  format,
} from 'date-fns'
import { useSearchCalendarEvents } from '../hooks/useCalendarEvents'
import { CalendarEventSearchParams, DEFAULT_EVENT_PAGE_SIZE } from '@api/types/calendarEvent'
import { CalendarEventCard } from '../components/CalendarEventCard'
import CalendarEventFilterDrawer from '../components/CalendarEventFilterDrawer'
import {
  CalendarHeader,
  CalendarViewMode,
  MonthCalendarView,
  WeekCalendarView,
  DayCalendarView,
  AgendaCalendarView,
} from '../components/calendar'

export function CalendarEventListPage() {
  const [viewMode, setViewMode] = useState<CalendarViewMode>('month')
  const [currentDate, setCurrentDate] = useState(new Date())
  const [searchTerm, setSearchTerm] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [listPage, setListPage] = useState(1)

  // Calculate date range based on view mode
  const dateRange = useMemo(() => {
    if (viewMode === 'month' || viewMode === 'agenda') {
      // Get the full calendar range (including days from adjacent months shown in grid)
      const monthStart = startOfMonth(currentDate)
      const monthEnd = endOfMonth(currentDate)
      return {
        start: startOfWeek(monthStart, { weekStartsOn: 0 }),
        end: endOfWeek(monthEnd, { weekStartsOn: 0 }),
      }
    } else if (viewMode === 'week') {
      return {
        start: startOfWeek(currentDate, { weekStartsOn: 0 }),
        end: endOfWeek(currentDate, { weekStartsOn: 0 }),
      }
    } else if (viewMode === 'day') {
      return {
        start: startOfDay(currentDate),
        end: endOfDay(currentDate),
      }
    }
    // List view - get upcoming events
    return { start: new Date(), end: null }
  }, [viewMode, currentDate])

  // Build search params
  const searchParams: CalendarEventSearchParams = useMemo(() => {
    const params: CalendarEventSearchParams = {
      sortBy: 'startDate',
      sortDirection: 'asc',
    }

    if (viewMode === 'list') {
      params.page = listPage
      params.pageSize = DEFAULT_EVENT_PAGE_SIZE
      if (searchTerm) {
        params.searchTerm = searchTerm
      }
    } else {
      // Calendar views - fetch all events in range
      params.startDateFrom = format(dateRange.start, "yyyy-MM-dd'T'HH:mm:ss")
      if (dateRange.end) {
        params.startDateTo = format(dateRange.end, "yyyy-MM-dd'T'HH:mm:ss")
      }
      params.pageSize = 100 // Get more events for calendar display
    }

    return params
  }, [viewMode, dateRange, listPage, searchTerm])

  const { data, isLoading } = useSearchCalendarEvents(searchParams)

  const events = data?.items || []
  const totalPages = data?.totalPages || 1
  const totalCount = data?.totalCount || 0

  // Handle search
  const handleSearch = () => {
    setListPage(1)
  }

  // Handle page change (list view)
  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setListPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Handle date click in calendar views
  const handleDateClick = (date: Date) => {
    setCurrentDate(date)
    setViewMode('day')
  }

  // Handle today button
  const handleToday = () => {
    setCurrentDate(new Date())
  }

  // Handle view mode change
  const handleViewModeChange = (mode: CalendarViewMode) => {
    setViewMode(mode)
  }

  // Count active filters
  const activeFilterCount = [searchTerm].filter(Boolean).length

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
        >
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              <Event sx={{ mr: 1, verticalAlign: 'middle' }} />
              Events Calendar
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Discover and join events in your community
            </Typography>
          </Box>
          <Button
            component={Link}
            to="/events/create"
            variant="contained"
            startIcon={<Add />}
          >
            Create Event
          </Button>
        </Stack>
      </Box>

      {/* Calendar Header with View Toggle */}
      <CalendarHeader
        currentDate={currentDate}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        onDateChange={setCurrentDate}
        onToday={handleToday}
      />

      {/* Search (for list view) */}
      {viewMode === 'list' && (
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              size="small"
              sx={{ flex: 1, maxWidth: 400 }}
            />
            <Button variant="outlined" onClick={handleSearch}>
              Search
            </Button>
            <IconButton
              onClick={() => setShowFilters(true)}
              color={activeFilterCount > 0 ? 'primary' : 'default'}
            >
              <FilterList />
              {activeFilterCount > 0 && (
                <Chip
                  label={activeFilterCount}
                  size="small"
                  color="primary"
                  sx={{ position: 'absolute', top: -8, right: -8, height: 20, fontSize: 12 }}
                />
              )}
            </IconButton>
          </Stack>
        </Box>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Calendar Views */}
      {!isLoading && viewMode === 'month' && (
        <MonthCalendarView
          currentDate={currentDate}
          events={events}
          onDateClick={handleDateClick}
        />
      )}

      {!isLoading && viewMode === 'week' && (
        <WeekCalendarView
          currentDate={currentDate}
          events={events}
          onDateClick={handleDateClick}
        />
      )}

      {!isLoading && viewMode === 'day' && (
        <DayCalendarView
          currentDate={currentDate}
          events={events}
        />
      )}

      {/* Agenda View */}
      {!isLoading && viewMode === 'agenda' && (
        <AgendaCalendarView
          events={events}
          currentDate={currentDate}
          onDateChange={setCurrentDate}
        />
      )}

      {/* List View */}
      {!isLoading && viewMode === 'list' && (
        <>
          {/* Results count */}
          {totalCount > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {totalCount} event{totalCount !== 1 ? 's' : ''} found
            </Typography>
          )}

          {/* Event Grid */}
          <Grid container spacing={3}>
            {events.length === 0 ? (
              // Empty state
              <Grid item xs={12}>
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Event sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No events found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {searchTerm
                      ? 'Try adjusting your search term'
                      : 'Be the first to create an event!'}
                  </Typography>
                  <Button
                    component={Link}
                    to="/events/create"
                    variant="contained"
                    startIcon={<Add />}
                  >
                    Create Event
                  </Button>
                </Box>
              </Grid>
            ) : (
              // Event cards
              events.map((event) => (
                <Grid item xs={12} sm={6} md={4} key={event.id}>
                  <CalendarEventCard event={event} />
                </Grid>
              ))
            )}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={listPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}

      {/* Filter Drawer */}
      <CalendarEventFilterDrawer
        open={showFilters}
        onClose={() => setShowFilters(false)}
        filters={searchParams}
        onFiltersChange={() => {}}
      />
    </Container>
  )
}

export default CalendarEventListPage
